import React, { useState } from 'react';

import { usePostSeverityMutation, useGetZapiConfigsQuery } from 'src/redux/services/centralApi';
import { Button, Checkbox, ColorPicker, Flex, Form, Input, Tooltip } from 'antd';
import type { Severity } from 'src/interfaces/entities/severity';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';

interface IAddSeverityForm extends Pick<Severity, 'name' | 'is_active' | 'client_id'> {
	notices: INotices[]
}

type INotices = 'in_dashboard' | 'in_telegram' | 'in_webhook' | 'in_zapi';

export default function AddSeverityPage() {
	const navigate = useNavigate();
	const [ color, setColor, ] = useState<string>('#21D375');

	const [ postSeverity, ] = usePostSeverityMutation();
	const { data: zapiConfigsData, } = useGetZapiConfigsQuery();

	const [ addSeverityForm, ] = Form.useForm<IAddSeverityForm>();

	const onFinishAddSeverityForm = ({
		is_active,
		name,
		notices,
	}: IAddSeverityForm) => {
		const creation_date = dayjs().format('YYYY-MM-DD');

		const _notices: Record<INotices, boolean> = {
			in_dashboard: false,
			in_telegram: false,
			in_webhook: false,
			in_zapi: false,
		};
		notices.forEach(key => {
			_notices[key] = true;
		});

		const _severity: Omit<Severity, 'severity_id' | 'client_id' | 'org_id'> = {
			name,
			color,
			is_active,
			creation_date,
			..._notices,
		};

		postSeverity(_severity)
			.unwrap()
			.then(() => {
				toast.success('Severidade criada com sucesso!', {
					autoClose: 1000,
				});
				navigate('/severidade/listar');
			})
			.catch((err) => {
				if (err?.status && err?.status === 500
					&& err?.data?.frontend_message) {
					toast.error(err?.data?.frontend_message, {
						autoClose: 1000,
					});
				}
			})
			.finally(() => {
				addSeverityForm.resetFields();
			});
	};

	return <DashboardTemplate pageTitle="Adicionar Severidade">
		<Form
			layout="vertical"
			onFinish={onFinishAddSeverityForm}
			form={addSeverityForm}
			style={{ marginTop: 10, }}
		>
			<Form.Item
				name={'name'}
				hasFeedback
				required
				tooltip={'Insira o nome da severidade.'}
				label={'Nome da severidade'}
				rules={[
					{
						required: true,
						message: 'Campo obrigatório.',
					},
				]}
			>
				<Input
					placeholder="Nome da severidade"
				/>
			</Form.Item>
			<Form.Item
				hasFeedback
				tooltip={'Insira a cor da severidade.'}
				label={'Cor'}
				rules={[
					{
						required: true,
						message: 'Campo obrigatório.',
					},
				]}
			>
				<ColorPicker
					style={{ width: '100%', }}
					onChange={(_, hex) => setColor(hex)}
				>
					<Button
						type='primary'
						style={{
							backgroundColor: color,
							width: '100%',
						}}
					>
						{color}
					</Button>
				</ColorPicker>
			</Form.Item>
			<Form.Item
				initialValue={[]}
				name='notices'
				tooltip={'Selecione por onde será notificado'}
				hasFeedback
				label="Notificações"
			>
				<Checkbox.Group
					options={[
						{
							label: <Flex>
								<div>
									Whatsapp
								</div>
								{
									!zapiConfigsData && <Tooltip
										title={
											<>
												Configure a API do whatsapp{' '}
												<Link
													to={`${process.env.REACT_APP_PUBLIC_URL}/comunicacao/whatsapp/configurar`}
												>
													aqui
												</Link>{' '}
												para ter acesso a essa
												funcionalidade.
											</>

										}
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 4,
												color: 'rgba(0,0,0,0.45)',
												cursor: 'help',
											}}
										/>
									</Tooltip>
								}
							</Flex>,
							value: 'in_zapi',
							disabled: !zapiConfigsData,
						},
						{ label: 'Telegram', value: 'in_telegram', },
						{ label: 'Dashboard', value: 'in_dashboard', },
						{ label: 'Webhook', value: 'in_webhook', },
					]}
				/>
			</Form.Item>
			<hr />
			<Form.Item
				initialValue={true}
				name='is_active'
				valuePropName={'checked'}
				hasFeedback
			>
				<Checkbox>Severidade ativa</Checkbox>
			</Form.Item>
			<Flex justify="flex-end" gap={8}>
				<Button
					onClick={() => {
						setColor('#21D375');
					}}
					htmlType="reset"
					type="text"
					icon={<ReloadOutlined />}
				/>
				<Button
					onClick={() => {
						addSeverityForm.submit();
					}}
					type="primary"
				>
					Criar severidade
				</Button>
			</Flex>
		</Form>
	</DashboardTemplate>;
}